import { environment } from "@environment";
import * as Sentry from "@sentry/react";

// Only initialize Sentry in production and staging environments
if (environment.env === "production") {
  const STATIC_VERSION = "stories-editor";
  Sentry.init({
    dsn: "https://50fb646426f8563da480bf6b932776ee@o4507845628592128.ingest.de.sentry.io/4509736542535760",

    // Environment-specific configuration
    environment: environment.env,
    release: STATIC_VERSION,

    // Performance optimizations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        // Optimize replay performance
        maskAllText: false,
        blockAllMedia: false,
        // Only capture replays for error sessions
        networkDetailAllowUrls: [window.location.origin],
        networkDetailDenyUrls: [
          // Exclude analytics and third-party services
          "https://api.mixpanel.com",
          "https://www.google-analytics.com",
          "https://www.googletagmanager.com",
        ],
      }),
    ],

    // Enhanced error filtering
    ignoreErrors: [
      // Browser-specific errors that are not actionable
      "The play() request was interrupted",
      "Failed to read a named property 'document' from 'Window'",
      "ResizeObserver loop completed with undelivered notifications.",
      "Cannot perform 'get' on a proxy that has been revoked",
      "Cannot read properties of undefined (reading 'nextSibling')",
      "The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.",
      "Event buffer exceeded maximum size of 20000000",
      "Could not stringify: illegal operation attempted on a revoked proxy",
      "Large HTTP payload",
      /^Blocked a frame with origin/,
      /^GET https:\/\/assets\.storydoc\.com\//,
      /Connection to Indexed Database server lost\./,
      /Connection to Liveblocks websocket server closed/,
      /%cLiveblocks background/,
      /Meta Pixel/,
      /TSS/,
      /slidesToShow/,
      /\[object Object\]/,
      "sandbox enabled: false",
      "User logged in, proceeding to generate outline",
      "FirebaseError",

      // Additional common browser errors
      "Script error.",
      "Script error for:",

      // Third-party library errors
      "Swiper Loop Warning:",
    ],

    // Optimized sampling rates for production
    tracesSampleRate: environment.env === "production" ? 0.1 : 0.2, // Reduced from 0.2 to 0.1 for production
    profilesSampleRate: environment.env === "production" ? 0.1 : 0.5, // Reduced from 0.5 to 0.1 for production

    // Session replay optimization
    replaysSessionSampleRate: environment.env === "production" ? 0.01 : 0.1, // Reduced from 0.1 to 0.01 for production
    replaysOnErrorSampleRate: 1.0, // Keep 100% for error sessions

    // Before send hook for additional filtering
    beforeSend(event, _hint) {
      // Only capture error and fatal level events
      if (event.level && !["error", "fatal"].includes(event.level)) {
        return null;
      }

      // Drop all events from /wizard page
      if (window.location.pathname.startsWith("/wizard")) {
        return null;
      }

      // Add custom context
      event.tags = {
        ...event.tags,
        environment: environment.env,
        version: STATIC_VERSION,
      };

      return event;
    },

    // Before send transaction hook for filtering traces
    beforeSendTransaction(event) {
      if (window.location.pathname.startsWith("/wizard")) {
        return null;
      }
      return event;
    },

    // Debug mode for staging
    debug: false,

    // Maximum breadcrumbs to reduce memory usage
    maxBreadcrumbs: 50,

    // Filter errors by URL origin
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /mixpanel\.com/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
      /doubleclick\.net/i,
    ],
    allowUrls: [/storydoc\.com/, /localhost/, /127\.0\.0\.1/],
  });
}
